// Marketing Landing Page with Modern SaaS Design
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Check,
  Close,
  Feedback,
  BugReport,
  Lightbulb,
  Article,
  Map,
  Code,
  Inventory2,
  Speed,
  Menu as MenuIcon,
  ArrowForward,
  Star,
} from '@mui/icons-material';
import { PLAN_PRICING, type PricingPlan } from '@/types';

// Logo Component
const Logo = ({ size = 40 }: { size?: number; color?: string }) => (
  <Box
    component="img"
    src="/favicon.png"
    alt="SupportSpace"
    sx={{
      width: size,
      height: size,
      borderRadius: 1,
    }}
  />
);

const FEATURES = [
  {
    icon: <Inventory2 sx={{ fontSize: 40 }} />,
    title: 'Multi-Product Support',
    description: 'Manage multiple products with separate branding, knowledge bases, and support queues from one dashboard.',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  {
    icon: <Code sx={{ fontSize: 40 }} />,
    title: '2-Minute Setup',
    description: 'Add to any site in seconds. React, Vue, Next.js, vanilla JS - works everywhere with one line of code.',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  },
  {
    icon: <Feedback sx={{ fontSize: 40 }} />,
    title: 'Visual Feedback',
    description: 'Capture screenshots and annotate directly on your site. Get context-rich bug reports every time.',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  },
  {
    icon: <BugReport sx={{ fontSize: 40 }} />,
    title: 'Smart Bug Tracking',
    description: 'Automatic console logs, session data, and user info captured with every report. Debug faster.',
    gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  },
  {
    icon: <Lightbulb sx={{ fontSize: 40 }} />,
    title: 'Feature Voting',
    description: 'Let users vote on features. Build what your customers actually want, backed by data.',
    gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  },
  {
    icon: <Article sx={{ fontSize: 40 }} />,
    title: 'Knowledge Base',
    description: 'Self-service help center reduces support tickets by 40%. Search-first experience.',
    gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  },
  {
    icon: <Map sx={{ fontSize: 40 }} />,
    title: 'Public Roadmap',
    description: 'Share your product roadmap publicly. Keep users engaged, informed, and excited.',
    gradient: 'linear-gradient(135deg, #5ee7df 0%, #b490ca 100%)',
  },
  {
    icon: <Speed sx={{ fontSize: 40 }} />,
    title: 'Lightweight Widget',
    description: 'Under 20KB gzipped. Zero impact on your site performance. Async loading.',
    gradient: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
  },
];

const PLANS: { name: PricingPlan; label: string; description: string; popular?: boolean }[] = [
  { name: 'free', label: 'Free', description: 'For side projects' },
  { name: 'pro', label: 'Pro', description: 'For growing teams', popular: true },
  { name: 'business', label: 'Business', description: 'For scaling companies' },
  { name: 'enterprise', label: 'Enterprise', description: 'Custom solutions' },
];

const PLAN_FEATURES_LIST: Record<PricingPlan, { text: string; included: boolean }[]> = {
  free: [
    { text: '1 project', included: true },
    { text: '2 team members', included: true },
    { text: '50 tickets/month', included: true },
    { text: '10 KB articles', included: true },
    { text: 'Basic widget', included: true },
    { text: 'Email support', included: true },
    { text: 'Custom branding', included: false },
    { text: 'API access', included: false },
  ],
  pro: [
    { text: '5 projects', included: true },
    { text: '10 team members', included: true },
    { text: '500 tickets/month', included: true },
    { text: '100 KB articles', included: true },
    { text: 'Screenshot capture', included: true },
    { text: 'Custom branding', included: true },
    { text: 'Feature voting', included: true },
    { text: 'Priority support', included: true },
    { text: 'API access', included: true },
  ],
  business: [
    { text: 'Unlimited projects', included: true },
    { text: '50 team members', included: true },
    { text: 'Unlimited tickets', included: true },
    { text: 'Unlimited KB articles', included: true },
    { text: 'Everything in Pro', included: true },
    { text: 'Custom domain', included: true },
    { text: 'Remove watermark', included: true },
    { text: 'SSO/SAML', included: true },
    { text: 'Audit logs', included: true },
  ],
  enterprise: [
    { text: 'Everything in Business', included: true },
    { text: 'Unlimited everything', included: true },
    { text: 'Custom CSS styling', included: true },
    { text: 'Dedicated support', included: true },
    { text: 'SLA guarantee', included: true },
    { text: 'Custom integrations', included: true },
    { text: 'On-premise option', included: true },
  ],
};

// Floating shapes for visual interest
const FloatingShapes = () => (
  <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
    {/* Large gradient orbs */}
    <Box
      sx={{
        position: 'absolute',
        top: '10%',
        left: '5%',
        width: 400,
        height: 400,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)',
        filter: 'blur(40px)',
        animation: 'float 8s ease-in-out infinite',
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-20px)' },
        },
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        top: '30%',
        right: '10%',
        width: 300,
        height: 300,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)',
        filter: 'blur(40px)',
        animation: 'float 10s ease-in-out infinite',
        animationDelay: '-2s',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: '10%',
        left: '20%',
        width: 250,
        height: 250,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(16,185,129,0.2) 0%, transparent 70%)',
        filter: 'blur(40px)',
        animation: 'float 12s ease-in-out infinite',
        animationDelay: '-4s',
      }}
    />
  </Box>
);

export const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll for navbar background
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Features', href: '#features' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Docs', href: '#docs' },
  ];

  return (
    <Box sx={{ bgcolor: '#0a0a1a', minHeight: '100vh' }}>
      {/* Navigation */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: scrolled ? alpha('#0a0a1a', 0.95) : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? `1px solid ${alpha('#fff', 0.1)}` : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Logo size={36} color="gradient" />
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                SupportSpace
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }} />

            {/* Desktop Navigation */}
            {!isMobile && (
              <Stack direction="row" spacing={1} alignItems="center">
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    href={item.href}
                    sx={{
                      color: alpha('#fff', 0.8),
                      '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.1) },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                <Button
                  sx={{
                    color: alpha('#fff', 0.8),
                    '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.1) },
                  }}
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/signup')}
                  sx={{
                    ml: 1,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                    },
                  }}
                >
                  Get Started Free
                </Button>
              </Stack>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{ color: '#fff' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#0a0a1a',
            width: 280,
            p: 3,
          },
        }}
      >
        <Stack spacing={2}>
          {navItems.map((item) => (
            <Button
              key={item.label}
              href={item.href}
              fullWidth
              sx={{ color: '#fff', justifyContent: 'flex-start' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              {item.label}
            </Button>
          ))}
          <Button
            fullWidth
            sx={{ color: '#fff', justifyContent: 'flex-start' }}
            onClick={() => {
              setMobileMenuOpen(false);
              navigate('/login');
            }}
          >
            Sign In
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setMobileMenuOpen(false);
              navigate('/signup');
            }}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            }}
          >
            Get Started Free
          </Button>
        </Stack>
      </Drawer>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: 16, md: 20 },
          pb: { xs: 10, md: 16 },
          overflow: 'hidden',
        }}
      >
        <FloatingShapes />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                icon={<Star sx={{ color: '#fbbf24 !important', fontSize: 16 }} />}
                label="Launch Special: 50% off first year"
                sx={{
                  mb: 3,
                  bgcolor: alpha('#fbbf24', 0.15),
                  color: '#fbbf24',
                  border: `1px solid ${alpha('#fbbf24', 0.3)}`,
                  fontWeight: 500,
                  '& .MuiChip-icon': { ml: 0.5 },
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 800,
                  lineHeight: 1.1,
                  mb: 3,
                  background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 50%, #c4b5fd 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Customer Feedback
                <br />
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Made Simple
                </Box>
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: alpha('#fff', 0.7),
                  mb: 4,
                  lineHeight: 1.6,
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                }}
              >
                Collect feedback, track bugs, and build features your users actually want.
                One widget. Zero friction. Half the price of alternatives.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/signup')}
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Start Free Trial
                </Button>
              </Box>

              <Stack direction="row" spacing={3} sx={{ color: alpha('#fff', 0.5) }}>
                <Typography variant="body2">✓ No credit card required</Typography>
                <Typography variant="body2">✓ 14-day free trial</Typography>
              </Stack>
            </Grid>

            {/* Hero Visual - Dashboard Preview */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: `0 40px 80px ${alpha('#000', 0.5)}`,
                  border: `1px solid ${alpha('#fff', 0.1)}`,
                  bgcolor: '#1a1a2e',
                  p: 2,
                  transform: { md: 'perspective(1000px) rotateY(-5deg) rotateX(5deg)' },
                  '&:hover': {
                    transform: { md: 'perspective(1000px) rotateY(0deg) rotateX(0deg)' },
                  },
                  transition: 'transform 0.5s ease',
                }}
              >
                {/* Mock Dashboard */}
                <Box sx={{ bgcolor: '#0f0f1a', borderRadius: 2, p: 2 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444' }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#fbbf24' }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#22c55e' }} />
                  </Box>

                  {/* Stats Cards */}
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    {[
                      { label: 'Open Tickets', value: '24', color: '#3b82f6' },
                      { label: 'Feature Votes', value: '1.2k', color: '#8b5cf6' },
                      { label: 'Satisfaction', value: '94%', color: '#10b981' },
                    ].map((stat) => (
                      <Grid size={{ xs: 4 }} key={stat.label}>
                        <Box
                          sx={{
                            bgcolor: alpha(stat.color, 0.1),
                            borderRadius: 2,
                            p: 1.5,
                            border: `1px solid ${alpha(stat.color, 0.2)}`,
                          }}
                        >
                          <Typography variant="h5" fontWeight="bold" sx={{ color: stat.color }}>
                            {stat.value}
                          </Typography>
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Ticket List Preview */}
                  {[
                    { title: 'Login button not working on mobile', type: 'Bug', priority: 'High' },
                    { title: 'Add dark mode support', type: 'Feature', priority: 'Medium' },
                    { title: 'Improve dashboard loading speed', type: 'Feedback', priority: 'Low' },
                  ].map((ticket, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: alpha('#fff', 0.03),
                        border: `1px solid ${alpha('#fff', 0.05)}`,
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor:
                            ticket.priority === 'High'
                              ? '#ef4444'
                              : ticket.priority === 'Medium'
                              ? '#fbbf24'
                              : '#22c55e',
                        }}
                      />
                      <Typography variant="body2" sx={{ color: '#fff', flex: 1, fontSize: '0.8rem' }}>
                        {ticket.title}
                      </Typography>
                      <Chip
                        label={ticket.type}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: alpha('#fff', 0.1),
                          color: alpha('#fff', 0.7),
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Social Proof */}
      <Box
        sx={{
          py: 6,
          borderTop: `1px solid ${alpha('#fff', 0.05)}`,
          borderBottom: `1px solid ${alpha('#fff', 0.05)}`,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="body2"
            sx={{ color: alpha('#fff', 0.4), textAlign: 'center', mb: 3 }}
          >
            Trusted by innovative teams worldwide
          </Typography>
          <Stack
            direction="row"
            spacing={{ xs: 4, md: 8 }}
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            sx={{ opacity: 0.4 }}
          >
            {['TechStartup', 'GrowthCo', 'InnovateLab', 'ScaleUp', 'CloudBase'].map((company) => (
              <Typography
                key={company}
                variant="h6"
                fontWeight="bold"
                sx={{ color: '#fff', letterSpacing: '0.05em' }}
              >
                {company}
              </Typography>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: { xs: 10, md: 16 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="Features"
              sx={{
                mb: 2,
                bgcolor: alpha('#3b82f6', 0.15),
                color: '#60a5fa',
                fontWeight: 500,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                color: '#fff',
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Everything You Need to
              <br />
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Understand Your Users
              </Box>
            </Typography>
            <Typography variant="h6" sx={{ color: alpha('#fff', 0.6), maxWidth: 600, mx: 'auto' }}>
              Powerful features that help you collect, organize, and act on customer feedback
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {FEATURES.map((feature) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={feature.title}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: alpha('#fff', 0.03),
                    border: `1px solid ${alpha('#fff', 0.08)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: alpha('#fff', 0.06),
                      borderColor: alpha('#fff', 0.15),
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 3,
                        background: feature.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        mb: 2,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff', mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.6), lineHeight: 1.6 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How it Works */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: `linear-gradient(180deg, ${alpha('#3b82f6', 0.05)} 0%, transparent 100%)`,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="How It Works"
              sx={{
                mb: 2,
                bgcolor: alpha('#10b981', 0.15),
                color: '#34d399',
                fontWeight: 500,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                color: '#fff',
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Up and Running in Minutes
            </Typography>
          </Box>

          <Stack spacing={4}>
            {[
              {
                step: 1,
                title: 'Sign Up & Create Projects',
                description: 'Create your account and set up a project for each product or website.',
                color: '#3b82f6',
              },
              {
                step: 2,
                title: 'Add the Widget',
                description: 'Copy your unique project code and paste it in your site. Each product gets its own Project ID.',
                color: '#8b5cf6',
              },
              {
                step: 3,
                title: 'Collect Feedback',
                description: 'Start receiving visual feedback, bug reports, and feature requests instantly.',
                color: '#10b981',
              },
            ].map((item) => (
              <Box
                key={item.step}
                sx={{
                  display: 'flex',
                  gap: 3,
                  alignItems: 'flex-start',
                  p: 3,
                  borderRadius: 3,
                  bgcolor: alpha('#fff', 0.03),
                  border: `1px solid ${alpha('#fff', 0.08)}`,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: alpha(item.color, 0.2),
                    color: item.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '1.25rem',
                    flexShrink: 0,
                    border: `2px solid ${item.color}`,
                  }}
                >
                  {item.step}
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff', mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: alpha('#fff', 0.6) }}>{item.description}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>

          {/* Code Example */}
          <Box
            sx={{
              mt: 6,
              p: 3,
              bgcolor: '#0f0f1a',
              borderRadius: 3,
              border: `1px solid ${alpha('#fff', 0.1)}`,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#fbbf24' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#22c55e' }} />
            </Box>
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), display: 'block', mb: 1 }}>
              Add this to your site (each product gets its own Project ID):
            </Typography>
            <Box
              component="pre"
              sx={{
                color: '#e2e8f0',
                m: 0,
                overflow: 'auto',
                fontSize: '0.875rem',
                fontFamily: '"Fira Code", monospace',
              }}
            >
              <Box component="span" sx={{ color: '#f472b6' }}>{`<script`}</Box>
              {'\n  '}
              <Box component="span" sx={{ color: '#a5b4fc' }}>src</Box>
              <Box component="span" sx={{ color: '#fff' }}>=</Box>
              <Box component="span" sx={{ color: '#86efac' }}>"https://cdn.supportspace.io/widget.js"</Box>
              {'\n  '}
              <Box component="span" sx={{ color: '#a5b4fc' }}>data-project-id</Box>
              <Box component="span" sx={{ color: '#fff' }}>=</Box>
              <Box component="span" sx={{ color: '#86efac' }}>"YOUR_PROJECT_ID"</Box>
              {'\n  '}
              <Box component="span" sx={{ color: '#a5b4fc' }}>data-key</Box>
              <Box component="span" sx={{ color: '#fff' }}>=</Box>
              <Box component="span" sx={{ color: '#86efac' }}>"YOUR_API_KEY"</Box>
              {'\n  '}
              <Box component="span" sx={{ color: '#a5b4fc' }}>async</Box>
              <Box component="span" sx={{ color: '#f472b6' }}>{`>`}</Box>
              {'\n'}
              <Box component="span" sx={{ color: '#f472b6' }}>{`</script>`}</Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ py: { xs: 10, md: 16 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="Pricing"
              sx={{
                mb: 2,
                bgcolor: alpha('#f59e0b', 0.15),
                color: '#fbbf24',
                fontWeight: 500,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                color: '#fff',
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography variant="h6" sx={{ color: alpha('#fff', 0.6) }}>
              Start free, upgrade as you grow. Cancel anytime.
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {PLANS.map((plan) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={plan.name}>
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    bgcolor: plan.popular ? alpha('#3b82f6', 0.1) : alpha('#fff', 0.03),
                    border: plan.popular
                      ? '2px solid #3b82f6'
                      : `1px solid ${alpha('#fff', 0.08)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: plan.popular ? '#60a5fa' : alpha('#fff', 0.2),
                    },
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="Most Popular"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
                      {plan.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.5), mb: 3 }}>
                      {plan.description}
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      {plan.name === 'enterprise' ? (
                        <Typography variant="h3" fontWeight="bold" sx={{ color: '#fff' }}>
                          Custom
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="h3" fontWeight="bold" sx={{ color: '#fff' }} component="span">
                            ${PLAN_PRICING[plan.name].annual / 100}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: alpha('#fff', 0.5) }}
                            component="span"
                          >
                            /seat/mo
                          </Typography>
                          {plan.name !== 'free' && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ color: alpha('#fff', 0.4) }}
                            >
                              billed annually
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>

                    <Button
                      variant={plan.popular ? 'contained' : 'outlined'}
                      fullWidth
                      sx={{
                        mb: 3,
                        ...(plan.popular
                          ? {
                              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                              },
                            }
                          : {
                              borderColor: alpha('#fff', 0.2),
                              color: '#fff',
                              '&:hover': {
                                borderColor: '#fff',
                                bgcolor: alpha('#fff', 0.1),
                              },
                            }),
                      }}
                      onClick={() =>
                        navigate(plan.name === 'enterprise' ? '/contact' : '/signup')
                      }
                    >
                      {plan.name === 'free'
                        ? 'Get Started'
                        : plan.name === 'enterprise'
                        ? 'Contact Sales'
                        : 'Start Trial'}
                    </Button>

                    <List dense sx={{ p: 0 }}>
                      {PLAN_FEATURES_LIST[plan.name].map((feature) => (
                        <ListItem key={feature.text} disablePadding sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            {feature.included ? (
                              <Check sx={{ fontSize: 18, color: '#10b981' }} />
                            ) : (
                              <Close sx={{ fontSize: 18, color: alpha('#fff', 0.2) }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={feature.text}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: {
                                color: feature.included ? alpha('#fff', 0.8) : alpha('#fff', 0.3),
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography
            variant="body2"
            sx={{ color: alpha('#fff', 0.4), textAlign: 'center', mt: 4 }}
          >
            All prices in USD. NZ-based customers will have 15% GST added.
          </Typography>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, #1e3a8a 0%, #312e81 50%, #4c1d95 100%)',
          }}
        />
        <FloatingShapes />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              color: '#fff',
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            Ready to Transform Your
            <br />
            Customer Feedback?
          </Typography>
          <Typography variant="h6" sx={{ color: alpha('#fff', 0.8), mb: 4 }}>
            Join thousands of teams collecting better feedback. Start free today.
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            onClick={() => navigate('/signup')}
            sx={{
              py: 2,
              px: 6,
              fontSize: '1.1rem',
              fontWeight: 600,
              bgcolor: '#fff',
              color: '#1e3a8a',
              '&:hover': {
                bgcolor: alpha('#fff', 0.9),
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Start Your Free Trial
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          py: 8,
          bgcolor: '#050510',
          borderTop: `1px solid ${alpha('#fff', 0.05)}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Logo size={32} color="gradient" />
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>
                  SupportSpace
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: alpha('#fff', 0.5), mb: 2 }}>
                Customer feedback and support made simple.
                <br />
                Built by Advancer Limited, New Zealand.
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2 }}>
                Product
              </Typography>
              <Stack spacing={1}>
                {['Features', 'Pricing', 'Integrations', 'Changelog'].map((item) => (
                  <Typography
                    key={item}
                    variant="body2"
                    sx={{
                      color: alpha('#fff', 0.5),
                      cursor: 'pointer',
                      '&:hover': { color: '#fff' },
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2 }}>
                Resources
              </Typography>
              <Stack spacing={1}>
                {['Documentation', 'API Reference', 'Blog', 'Status'].map((item) => (
                  <Typography
                    key={item}
                    variant="body2"
                    sx={{
                      color: alpha('#fff', 0.5),
                      cursor: 'pointer',
                      '&:hover': { color: '#fff' },
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2 }}>
                Company
              </Typography>
              <Stack spacing={1}>
                {['About', 'Contact', 'Privacy', 'Terms'].map((item) => (
                  <Typography
                    key={item}
                    variant="body2"
                    sx={{
                      color: alpha('#fff', 0.5),
                      cursor: 'pointer',
                      '&:hover': { color: '#fff' },
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Stack>
            </Grid>
          </Grid>

          <Typography
            variant="body2"
            sx={{
              mt: 6,
              pt: 3,
              borderTop: `1px solid ${alpha('#fff', 0.1)}`,
              color: alpha('#fff', 0.4),
            }}
          >
            © {new Date().getFullYear()} SupportSpace by Advancer Limited. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
